import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '../shared/constants';

/**
 * SEO Component for managing meta tags, Open Graph, Twitter Cards, and structured data
 * 
 * @param {Object} props
 * @param {String} props.title - Page title (defaults to site name)
 * @param {String} props.description - Meta description
 * @param {String} props.keywords - Meta keywords (comma-separated)
 * @param {String} props.image - OG/Twitter image URL
 * @param {String} props.type - OG type (website, article, etc.)
 * @param {String} props.url - Canonical URL (defaults to current location)
 * @param {Object} props.structuredData - JSON-LD structured data object
 * @param {String} props.lang - Language code (defaults to current i18n language)
 */
export default function SEO({
  title,
  description,
  keywords,
  image = '/logo.svg',
  type = 'website',
  url,
  structuredData,
  lang
}) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const currentLang = lang || i18n.language;
  
  // Base site information
  const siteName = 'trip147';
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://trip147.com';
  const fullUrl = url || `${siteUrl}${location.pathname}${location.search}`;
  
  // Default values
  const defaultTitle = t('common.siteTitle', { defaultValue: 'trip147 - Plan Your Perfect Trip' });
  const defaultDescription = t('common.siteDescription', { 
    defaultValue: 'trip147 is your all-in-one platform to manage, organize, and easily share your travel itinerary. Keep every detail of your trip in one place—hotels, attractions, transportation, costs, to-do lists, and more.' 
  });
  
  const pageTitle = title ? `${title} | ${siteName}` : defaultTitle;
  const metaDescription = description || defaultDescription;
  
  // Default OG image
  const ogImage = image.startsWith('http') ? image : `${siteUrl}${image}`;
  
  // Get ogLocale from language constants
  const language = LANGUAGES.find(lang => lang.code === currentLang);
  const ogLocale = language?.ogLocale || 'en_US';
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={metaDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={ogLocale} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content="@trip147" />
      
      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}
